import { Component } from "@theme/component";

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ["disclosureTrigger", "disclosureContent"];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Expands the panel if closed. Idempotent.
   */
  open() {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    if (expanded) return;
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute("aria-label", `${trigger.dataset.disclosureClose}`);
    content.inert = false;
  }

  /**
   * Collapses the panel if open. Idempotent; fires `disclosure:close` when transitioning closed
   * so nested content (e.g. cart discount errors) can reset.
   */
  close() {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    if (!expanded) return;
    this.dispatchEvent(new CustomEvent("disclosure:close", { bubbles: true }));
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-label", `${trigger.dataset.disclosureOpen}`);
    content.inert = true;
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    if (expanded) {
      this.close();
      return;
    }
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute("aria-label", `${trigger.dataset.disclosureClose}`);
    content.inert = false;
  };
}

if (!customElements.get("disclosure-custom")) {
  customElements.define("disclosure-custom", DisclosureCustom);
}
